import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-purchase-order-detail',
  templateUrl: './purchase-order-detail.component.html',
  styleUrls: ['./purchase-order-detail.component.css']
})
export class PurchaseOrderDetailComponent implements OnInit {
  loading = true;
  busy = false;
  po: any = null;
  receivedMode = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private toastr: ToastrMsgService
  ) {}

  ngOnInit(): void {
    this.receivedMode = this.router.url.includes('received-purchase-orders');
    this.reload();
  }

  status(): string {
    return String(this.po?.po_status || 'draft').toLowerCase();
  }

  canIssue(): boolean {
    return !this.receivedMode && this.status() === 'draft';
  }

  canCancel(): boolean {
    return !this.receivedMode && ['draft', 'issued', 'rejected'].includes(this.status());
  }

  canAmend(): boolean {
    return !this.receivedMode && ['issued', 'accepted', 'amended'].includes(this.status());
  }

  canAccept(): boolean {
    return this.receivedMode && this.status() === 'issued';
  }

  canReject(): boolean {
    return this.receivedMode && this.status() === 'issued';
  }

  canCreateInvoice(): boolean {
    return this.receivedMode && ['accepted', 'amended'].includes(this.status());
  }

  formatCurrency(amount: number | string): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(Number(amount) || 0);
  }

  back(): void {
    this.router.navigate([this.receivedMode ? '/home/received-purchase-orders' : '/home/purchase-order-list']);
  }

  reload(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.toastr.error('Invalid purchase order');
      this.loading = false;
      return;
    }
    this.loading = true;
    this.apiService.getPurchaseOrderById(id).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.po = res?.data || res;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err.error?.message || err.error?.error || 'Purchase order not found');
      }
    });
  }

  issue(): void {
    this.runAction(() => this.apiService.issuePurchaseOrder(this.po.id), 'Purchase order issued');
  }

  cancel(): void {
    this.runAction(() => this.apiService.cancelPurchaseOrder(this.po.id), 'Purchase order cancelled');
  }

  accept(): void {
    this.runAction(() => this.apiService.acceptPurchaseOrder(this.po.id), 'Purchase order accepted');
  }

  reject(): void {
    this.runAction(() => this.apiService.rejectPurchaseOrder(this.po.id), 'Purchase order rejected');
  }

  amend(): void {
    this.router.navigate(['/home/add-po-amendment'], { queryParams: { po_id: this.po.id } });
  }

  createInvoice(): void {
    this.router.navigate(['/home/add-invoice'], {
      queryParams: { po_id: this.po.id, project_id: this.po.project_id || undefined }
    });
  }

  viewInvoice(invoice: { id: number }): void {
    this.router.navigate(['/home/edit-invoice', invoice.id]);
  }

  private runAction(request: () => any, success: string): void {
    if (!this.po?.id || this.busy) return;
    this.busy = true;
    request().subscribe({
      next: (res: any) => {
        this.busy = false;
        this.toastr.success(res?.message || success);
        this.po = res?.data || this.po;
        this.reload();
      },
      error: (err: any) => {
        this.busy = false;
        this.toastr.error(err?.error?.message || err?.error?.error || 'Action failed');
      }
    });
  }
}
